import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { guides, guidesNewestFirst, guideTopics, guideDateLabel, guidePath, guideImages, editorialImagePath, educationGuides, educationGuideContents, type EducationGuideSlug } from '../../../packages/shared/src/guides.js';
import { DecantProviders } from './components/DecantProviders.js';
import type { Country } from './types.js';
import './guides.css';

const autumn = guides.find((guide) => guide.slug === 'our-picks-autumn-2026')!;
const testing = guides.find((guide) => guide.slug === 'testing-scents-101')!;
export function GuideTrail({ article = false, current = 'Autumn 2026' }: { article?: boolean; current?: string }) {
  return <nav className="catalogue-trail" aria-label="Breadcrumb"><ol>
    <li><Link to="/">Explore</Link></li>
    <li>{article ? <Link to="/guides">Guides</Link> : <span aria-current="page">Guides</span>}</li>
    {article && <li><span aria-current="page">{current}</span></li>}
  </ol></nav>;
}
const picks = [
  {
    id: 'rose-and-soft-woods',
    direction: 'Rose & soft woods',
    notes: 'Rose · Sandalwood · Vanilla',
    name: 'French Avenue Spirito Mystic',
    layout: 'aside',
    path: '/fragrances/french-avenue-spirito-mystic-extrait-de-parfum',
    paragraphs: [
      'Rose and cardamom lead into powdery sandalwood, tonka and vanilla. This Extrait de Parfum is the floral pick for autumn, with soft woods adding warmth.',
    ],
  },
  {
    id: 'citrus-and-praline',
    direction: 'Citrus & praline',
    notes: 'Blackcurrant · Praline · Vetiver',
    name: 'Khadlaj Karus Gold Absolu',
    layout: 'mirror',
    path: '/fragrances/khadlaj-karus-gold-absolu-eau-de-parfum',
    paragraphs: [
      'Candied lemon, bergamot and blackcurrant bring brightness to praline and soft florals. Musk and vetiver round out this Eau de Parfum: a choice for autumn days when you still want some freshness.',
    ],
  },
  {
    id: 'spiced-toffee',
    direction: 'Spiced toffee',
    notes: 'Cardamom · Toffee · Amberwood',
    name: 'Rayhaan Wolf',
    layout: 'wide',
    path: '/fragrances/rayhaan-wolf-eau-de-parfum',
    paragraphs: [
      'Cardamom, toffee and amberwood make Wolf the sweet, spicy evening pick. Choose this Eau de Parfum if you want a warmer direction as the weather cools.',
    ],
  },
];

const testingSections = [
  { id: 'in-store', title: 'Try it in store', summary: 'Make the most of a first impression' },
  { id: 'decants', title: 'Take a decant home', summary: 'Live with the scent for longer' },
  { id: 'compare', title: 'Compare what matters', summary: 'Opening · Settled scent · Wear' },
  { id: 'choose', title: 'Before you buy', summary: 'Decide after a few wears' },
];

// Jump links for each guide's sections, shown in the index's lead panel when that guide is the latest.
const guideSections: Record<string, { id: string; title: string }[]> = {
  [testing.slug]: testingSections,
  [autumn.slug]: [...picks.map((pick) => ({ id: pick.id, title: pick.direction })), { id: 'choosing', title: 'Before you choose' }],
  ...Object.fromEntries((Object.keys(educationGuides) as EducationGuideSlug[]).map((slug) => [slug, educationGuideContents(slug)])),
};

// Highlights the contents link for the section currently being read.
export function useActiveSection(ids: string[]) {
  const [active, setActive] = useState(ids[0]);
  const key = ids.join();
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const line = window.innerHeight * 0.3;
      let current = ids[0];
      for (const id of ids) {
        const element = document.getElementById(id);
        if (element && element.getBoundingClientRect().top <= line) current = id;
      }
      setActive(current);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule); };
  }, [key]);
  return active;
}

function GuideFigure({ pick }: { pick: string }) {
  const image = guideImages.find((item) => item.pick === pick);
  if (!image) return null;
  const src = editorialImagePath(image.key);
  return (
    <figure className="guide-figure">
      <img src={src} srcSet={`${src}?size=thumb 480w, ${src} 1200w`} sizes="(max-width: 1180px) calc(100vw - 8%), 600px" width={1200} height={800} alt={image.alt} loading={pick === picks[0].id ? 'eager' : 'lazy'} decoding="async" />
    </figure>
  );
}

export function Guides() {
  const [topic, setTopic] = useState<string | null>(null);
  const [filtered, setFiltered] = useState(false);
  const lead = guidesNewestFirst[0];
  const leadImage = guideImages.find((item) => item.guide === lead.slug);
  const leadSrc = leadImage && editorialImagePath(leadImage.key);
  const leadSections = guideSections[lead.slug] ?? [];
  const listed = topic ? guidesNewestFirst.filter((guide) => (guide.topics as readonly string[]).includes(topic)) : guidesNewestFirst;
  const choose = (next: string | null) => { setTopic(next); setFiltered(true); };
  return (
    <section className="guides-page guides-index">
      <GuideTrail />
      <div className="guide-ledger-layout">
        <header className="guides-heading guide-index-heading">
          <h1>Guides</h1>
          <p>Practical advice and editorial picks to help you find, test and choose fragrances.</p>
        </header>
        <aside className="guide-lead" aria-label="Latest guide">
          {leadImage && leadSrc && (
            <Link className="guide-lead-image" to={guidePath(lead.slug)} tabIndex={-1} aria-hidden="true">
              <img src={leadSrc} srcSet={`${leadSrc}?size=thumb 480w, ${leadSrc} 1200w`} sizes="(max-width: 700px) 112px, 144px" width={leadImage.width} height={leadImage.height} style={{ objectPosition: leadImage.position }} alt="" decoding="async" />
            </Link>
          )}
          <div className="guide-lead-copy">
            <h2><Link to={guidePath(lead.slug)}>{lead.title}</Link></h2>
            <p className="guide-meta"><time dateTime={lead.publishedAt}>{guideDateLabel(lead.publishedAt)}</time></p>
            <p className="guide-lead-line">{lead.description}</p>
            <p className="guide-topics">{lead.topics.join(' · ')}</p>
            {leadSections.length > 0 && (
              <nav className="guide-lead-sections" aria-label={`Sections in ${lead.title}`}>
                <h3>In this guide</h3>
                <ol>
                  {leadSections.map((section) => (
                    <li key={section.id}><Link to={`${guidePath(lead.slug)}#${section.id}`}>{section.title}</Link></li>
                  ))}
                </ol>
              </nav>
            )}
            <Link className="button" to={guidePath(lead.slug)}>Read the guide</Link>
          </div>
        </aside>
        <section className="guide-ledger" aria-labelledby="guide-ledger-title">
          <div className="guide-filter" role="group" aria-label="Filter guides by topic">
            <button type="button" aria-pressed={topic === null} onClick={() => choose(null)}>All guides<span className="guide-filter-count">{guidesNewestFirst.length}</span></button>
            {guideTopics.map((name) => (
              <button type="button" key={name} aria-pressed={topic === name} onClick={() => choose(topic === name ? null : name)}>{name}<span className="guide-filter-count">{guidesNewestFirst.filter((guide) => (guide.topics as readonly string[]).includes(name)).length}</span></button>
            ))}
          </div>
          <div className="guide-ledger-head">
            <h2 id="guide-ledger-title">{topic ?? 'All guides'}</h2>
            <p className="guide-count" aria-live="polite">{listed.length} {listed.length === 1 ? 'guide' : 'guides'}</p>
          </div>
          <ol className={filtered ? 'guide-rows guide-rows--settle' : 'guide-rows'} key={topic ?? 'all'}>
            {listed.map((guide) => {
              const isLead = guide.slug === lead.slug;
              return (
                <li key={guide.slug}>
                  <Link to={guidePath(guide.slug)} className={isLead ? 'guide-row guide-row--latest' : 'guide-row'}>
                    <span className="guide-row-date"><time dateTime={guide.publishedAt}><span className="guide-row-day">{guideDateLabel(guide.publishedAt).split(' ')[0]}</span> <span>{guideDateLabel(guide.publishedAt).split(' ').slice(1).join(' ')}</span></time>{isLead && <em>Latest</em>}</span>
                    <span className="guide-row-main">
                      <strong>{guide.title}</strong>
                      <span>{guide.description}</span>
                      <small>{guide.topics.join(' · ')}</small>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ol>
        </section>
      </div>
    </section>
  );
}

export function TestingScentsGuide({
  initialCountry,
  countries,
}: {
  initialCountry: string;
  countries: Country[];
}) {
  const [country, setCountry] = useState(initialCountry);
  useEffect(() => setCountry(initialCountry), [initialCountry]);
  const image = guideImages.find((item) => item.key === 'testing-scents-101-decant')!;
  const imageSrc = editorialImagePath(image.key);
  const active = useActiveSection(testingSections.map((section) => section.id));
  return (
    <article className="guides-page guide-article testing-guide">
      <GuideTrail article current="Testing scents 101" />
      <header className="guides-heading guide-article-heading">
        <div className="guide-heading-main"><h1>{testing.title}</h1><time className="guide-date" dateTime={testing.publishedAt}>{guideDateLabel(testing.publishedAt)}</time></div>
        <div className="guide-standfirst"><p>{testing.description}</p></div>
      </header>
      <div className="guide-layout">
        <nav className="guide-contents" aria-label="In this guide">
          <h2>In this guide</h2>
          {testingSections.map((section) => <a key={section.id} href={`#${section.id}`} aria-current={active === section.id ? 'true' : undefined}><span>{section.title}</span></a>)}
        </nav>
        <div className="guide-body testing-guide-body">
          <section id="in-store" className="testing-guide-section" aria-labelledby="in-store-title">
            <h2 id="in-store-title">Try it in store</h2>
            <p>A fragrance counter is a good place to narrow your choices. Start on a blotter to see which scents interest you, then try your favourites on skin. Leave some space between each application so the scents do not compete.</p>
            <p>Give the fragrance time to settle before deciding. The first few minutes are only the opening; the scent you wear later may feel softer, warmer, or woodier. If you can, step outside the store and check it again during your day.</p>
          </section>
          <section id="decants" className="testing-guide-section testing-guide-decants" aria-labelledby="decants-title">
            <div className="testing-guide-decant-intro">
              <div>
                <h2 id="decants-title">Take a decant home</h2>
                <p>A decant is a small amount of fragrance taken from a larger bottle. It lets you try a scent in everyday life before committing to a full bottle. Buying a fragrance without trying it first is called blind buying; a decant helps you decide whether it is worth the cost.</p>
                <p>Check whether your sample has a spray or a dab applicator. Start with one or two sprays on clean, dry skin, or apply a small amount from a dab vial to your wrist. Let it dry naturally. Wear it more than once if the sample allows.</p>
                <p>Close the cap firmly after use, and keep the vial upright in a cool, dry place away from heat and direct sunlight.</p>
              </div>
              <figure><img src={imageSrc} srcSet={`${imageSrc}?size=thumb 480w, ${imageSrc} 1200w`} sizes="(max-width: 1050px) min(640px, calc(100vw - 8%)), 400px" width={image.width} height={image.height} alt={image.alt} loading="lazy" decoding="async" /></figure>
            </div>
            <DecantProviders country={country} countries={countries} onCountryChange={setCountry} />
          </section>
          <section id="compare" className="testing-guide-section" aria-labelledby="compare-title">
            <h2 id="compare-title">Compare what matters</h2>
            <p>If you are choosing between two fragrances, wear each on separate days under similar conditions. Use roughly the same amount, then note what you enjoy about the opening, the settled scent, and how long you can still notice it.</p>
            <p>Weather, setting, and how much you apply can affect your impression. Bottles from different production batches may also smell or perform slightly differently, so a sample or someone else's review may not exactly match the bottle you buy. A note list or a quick spray in the air cannot tell you everything about how a fragrance wears on your skin.</p>
          </section>
          <section id="choose" className="guide-closing" aria-labelledby="choose-title">
            <h2 id="choose-title">Before you buy</h2>
            <div>
              <p>After a few wears, ask yourself:</p>
              <ul className="testing-guide-checks">
                <li><Check size={18} aria-hidden />Do you still enjoy the scent after several hours?</li>
                <li><Check size={18} aria-hidden />Would you reach for it again?</li>
                <li><Check size={18} aria-hidden />If you are comparing two fragrances, do the differences matter to you?</li>
              </ul>
              <Link className="text-link" to="/">Explore the fragrance collection</Link>
            </div>
          </section>
        </div>
      </div>
    </article>
  );
}

export function AutumnGuide() {
  const active = useActiveSection([...picks.map((pick) => pick.id), 'choosing']);
  return (
    <article className="guides-page guide-article">
      <GuideTrail article />
      <header className="guides-heading guide-article-heading">
        <div className="guide-heading-main"><h1>{autumn.title}</h1><time className="guide-date" dateTime={autumn.publishedAt}>{guideDateLabel(autumn.publishedAt)}</time></div>
        <div className="guide-standfirst">
          <p>{autumn.description}</p>
          <p className="guide-editorial-note">These picks are organised by their recorded notes. Try a sample to judge how each one wears on your skin.</p>
        </div>
      </header>
      <div className="guide-layout">
        <nav className="guide-contents" aria-label="In this guide">
          <h2>In this guide</h2>
          {picks.map((pick) => <a key={pick.id} href={`#${pick.id}`} aria-current={active === pick.id ? 'true' : undefined}><span>{pick.direction}<small>{pick.name}</small></span></a>)}
          <a href="#choosing" aria-current={active === 'choosing' ? 'true' : undefined}><span>Before you choose</span></a>
        </nav>
      <div className="guide-body">
        {picks.map((pick) => (
          <section className={`guide-pick guide-pick--${pick.layout}`} key={pick.id} aria-labelledby={pick.id}>
            <header className="guide-pick-head">
              <h2 id={pick.id}>{pick.direction}</h2>
              <h3>{pick.name}</h3>
            </header>
            <GuideFigure pick={pick.id} />
            <div className="guide-pick-copy">
              {pick.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </div>
            <aside className="guide-pairing" aria-label={`${pick.name} at a glance`}>
                <dl>
                  <div><dt>Key notes</dt><dd>{pick.notes}</dd></div>
                </dl>
                <Link className="text-link" to={pick.path}>View fragrance</Link>
            </aside>
          </section>
        ))}
        <section className="guide-closing" aria-labelledby="choosing">
          <h2 id="choosing">Before you choose</h2>
          <div>
            <p>Try a sample and let it settle on your skin before deciding which notes and wear style suit you.</p>
            <Link className="text-link" to="/">Explore the fragrance collection</Link>
          </div>
        </section>
      </div>
      </div>
    </article>
  );
}
